import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  IAddItemRequest,
  useAddToCart,
  useDeleteCartItem,
  useGetCart,
  useSetCartItemQuantity,
} from '../../api/cart';
import useContext from '../../hooks/useContext';
import useToggle from '../../hooks/useToggle';

const CART_ID_LOCAL_STORAGE_KEY = 'CART_ID';

const getStoredCartId = () => localStorage.getItem(CART_ID_LOCAL_STORAGE_KEY);

const getQuantity = (items: Commerce.ICartItem[]) =>
  items.reduce((sum, { quantity }) => sum + quantity, 0);

export const createCartKey = (cart?: Commerce.ICart) => {
  const key = cart?.items
    .sort((a, b) => a.key.localeCompare(b.key))
    .reduce((acc, item) => `${acc}${item.key}:${item.quantity}`, '');

  if (!cart?.coupon?.value) {
    return key;
  }

  return `${key}:${cart.coupon.value}`;
};

interface ICartContext {
  adding: boolean;
  addingId?: IAddItemRequest;
  addToCart: (variantId: string, quantity?: number) => Promise<void>;
  busy: boolean;
  cart?: Commerce.ICart;
  cartId: string | null;
  deleteCart: () => void;
  deleteCartItem: (id: string) => void;
  deletingItem?: string;
  cartError?: Commerce.CommerceException | null;
  fetching: boolean;
  items?: Commerce.ICartItem[];
  unsellableItems: Commerce.ICartItem[];
  key?: string;
  quantity: number;
  reset: () => void;
  setQuantity: (key: string, quantity: number) => void;
  settingQuantity: boolean;
  tokenError: boolean;
  resetTokenError: () => void;
  setTokenError: () => void;
}

const CartContext = createContext<ICartContext | undefined>(undefined);

interface ICartProvider {
  children: ReactNode;
}

export const CartProvider = ({ children }: ICartProvider) => {
  const [cartId, setCartId] = useState(getStoredCartId());
  const queryClient = useQueryClient();
  const { cart, loading: fetching, error: getCartError } = useGetCart(cartId);
  const { deleteItem, loading: deleting } = useDeleteCartItem(cartId);
  const [deletingItem, setDeletingItem] = useState<string | undefined>();
  const addToCartMutation = useAddToCart(cartId);
  const setCartItemQuantityMutation = useSetCartItemQuantity(cartId);
  const [tokenError, , , resetTokenError, setTokenError] = useToggle(false);

  const updateCartId = (cartId: null | string) => {
    if (cartId) {
      localStorage.setItem(CART_ID_LOCAL_STORAGE_KEY, cartId);
      setCartId(cartId);
    } else {
      localStorage.removeItem(CART_ID_LOCAL_STORAGE_KEY);
      setCartId(null);
      queryClient.setQueryData(['cart'], undefined);
    }
  };

  const deleteCartItem = useCallback((id: string) => {
    setDeletingItem(id);
    deleteItem(id).finally(() => setDeletingItem(undefined));
  }, []);

  const deleteCart = useCallback(() => {
    updateCartId(null);
  }, []);

  useEffect(() => {
    if (getCartError?.type === 'CartNotFoundException') {
      deleteCart();
    }
  }, [getCartError]);

  const addToCart = useCallback((variantId: string, quantity = 1) => {
    return (
      addToCartMutation
        .addToCart({ variantId, quantity })
        .then(({ cart: { id } }) => updateCartId(id))
        // If cart not found, delete cart id and let user try again
        // which will generate a new cart
        .catch((err: unknown) => {
          if ((err as Commerce.ICartError)?.type === 'CartNotFoundException') {
            deleteCart();
          }
        })
    );
  }, []);

  const setQuantity = useCallback((variantId: string, quantity: number) => {
    setCartItemQuantityMutation.setQuantity({ variantId, quantity });
  }, []);

  const quantity = useMemo(() => (cart && getQuantity(cart.items)) || 0, [cart]);

  const key = useMemo(() => createCartKey(cart), [cart]);

  const unsellableItems = useMemo(
    () => (cart && cart?.items.filter((item) => !item?.sellable)) || [],
    [cart],
  );

  const cartError = addToCartMutation.error || setCartItemQuantityMutation.error;

  const reset = () => {
    addToCartMutation.reset();
    setCartItemQuantityMutation.reset();
  };

  const value = useMemo(
    () => ({
      adding: addToCartMutation.loading,
      addingId: addToCartMutation.addingId,
      addToCart: addToCart,
      busy: fetching || deleting || addToCartMutation.loading,
      cart,
      cartId,
      deleteCart,
      deleteCartItem,
      deletingItem,
      cartError,
      fetching,
      items: cart?.items,
      unsellableItems,
      key,
      quantity,
      reset,
      setQuantity,
      settingQuantity: setCartItemQuantityMutation.loading,
      tokenError,
      resetTokenError,
      setTokenError,
    }),
    [
      addToCartMutation.loading,
      addToCartMutation.addingId,
      addToCart,
      fetching || deleting,
      cart,
      cartError,
      cartId,
      deleteCart,
      deleteCartItem,
      deletingItem,
      unsellableItems,
      key,
      quantity,
      setCartItemQuantityMutation.loading,
      tokenError,
      resetTokenError,
      setTokenError,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

const useCart = () => useContext(CartContext);

export default useCart;
